import axios from 'axios';
import SecureStorage from 'react-secure-storage';

// 환경별 API 서버 URL
export const devURL = "https://dev-ori-backend.rokithc.com/";
export const prdURL = "https://prd-ori-backend.rokithc.com/";
export const localURL = "http://10.10.21.108:8080/";

const baseURL = devURL;

const Api = axios.create({
  baseURL: baseURL,
  headers: {},
});

export const noInterceptorApi = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** 요청 인터셉터 - authRequired가 true인 경우 자동으로 Authorization 헤더에 토큰 추가*/
Api.interceptors.request.use(
  (config) => {
    if (config.authRequired) {
      const token = SecureStorage.getItem('accessToken');
      const uid = SecureStorage.getItem('uid');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
        config.headers['uid'] = uid;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);
// 에러 메시지 상수
const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';
// 토큰 갱신 중 중복 요청 방지 플래그
let isRefreshingToken = false;

/**
 * 응답 인터셉터
 * - 401 에러: 토큰 만료 시 자동 갱신 시도
 * - 403 에러: 권한 없음 처리
 * - 기타 에러: 서버 에러 처리
 */
Api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 401 Unauthorized: 인증 실패
    if (error.response && error.response.status === 401) {
      const statusCode = error.response.data?.statusCode;

      // 232: Refresh Token 만료 (재로그인 필요)
      if (statusCode === 232) {
        if (!isRefreshingToken) {
          isRefreshingToken = true;
          clearLocalStorageAndLogout();
          alert(SESSION_EXPIRED_MESSAGE);
        }
        return Promise.reject(error);
      }
      // 231: Access Token 만료 (토큰 갱신 시도)
      else if (statusCode === 231) {
        const reIssueResult = await ReIssueToken();
        if (reIssueResult) {
          // 토큰 갱신 성공 시 원래 요청 재시도
          originalRequest.headers['Authorization'] = `Bearer ${SecureStorage.getItem('accessToken')}`;
          isRefreshingToken = false;
          return axios(originalRequest);
        } else {
          // 토큰 갱신 실패 시 로그아웃
          if (!isRefreshingToken) {
            isRefreshingToken = true;
            clearLocalStorageAndLogout();
            alert(SESSION_EXPIRED_MESSAGE);
          }
          return Promise.reject(error);
        }
      }
      // 기타 401 에러
      else {
        if (!isRefreshingToken) {
          isRefreshingToken = true;
          const errorMessage = error.response.data?.message || 'sessionExpired';
          clearLocalStorageAndLogout();
          alert(SESSION_EXPIRED_MESSAGE);
        }
        return Promise.reject(error);
      }
    }
    // 403 Forbidden: 권한 없음
    else if (error.response && error.response.status === 403) {
      if (!isRefreshingToken) {
        isRefreshingToken = true;
        alert('403 Forbidden.');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
    // 기타 서버 에러 (404 제외)
    else {
      if (error.response && error.response.status !== 404) {
        if (!isRefreshingToken) {
          isRefreshingToken = true;
          alert('Server Error. Please contact the RokitHealthCare Developer.');
        }
        return error.response.data;
      }
    }

    return Promise.reject(error);
  },
);

/**
 * 토큰 재발급 함수
 * - Access Token이 만료되었을 때 Refresh Token을 사용하여 새 토큰 발급
 * - noInterceptorApi 사용으로 무한 루프 방지
 * 
 * @returns {Promise<boolean>} 토큰 갱신 성공 여부
 */
const ReIssueToken = async () => {
  try {
    const response = await noInterceptorApi.post(
      `/users/re-issue`,
      {
        accessToken: SecureStorage.getItem('accessToken'),
        refreshToken: SecureStorage.getItem('refreshToken'),
        email: SecureStorage.getItem('email'),
      },
      {
        headers: {
          Authorization: `Bearer ${SecureStorage.getItem('refreshToken')}`,
          uid: SecureStorage.getItem('uid'),
        },
      },
    );

    if (response.status === 200) {
      // 새로 발급받은 토큰 저장
      SecureStorage.setItem(
        'accessToken',
        response.data.data.tokenInfo.accessToken,
      );
      SecureStorage.setItem(
        'refreshToken',
        response.data.data.tokenInfo.refreshToken,
      );
      return true;
    }
    return false;
  } catch (e) {
    window.location.href = '/login';
    return false;
  }
};

/**
 * GET 요청 (인증 필요)
 * @param {string} url - API 엔드포인트
 * @param {object} params - 쿼리 파라미터
 * @returns {Promise} 응답 데이터 또는 에러
 */
export const getApi = async (url, params) => {
  try {
    const response = await Api.get(url, {
      params: params,
      authRequired: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (e) {
    return e;
  }
};

/**
 * POST 요청 (인증 필요)
 * @param {string} url - API 엔드포인트
 * @param {object} data - 요청 본문 데이터
 * @returns {Promise} 응답 데이터 또는 에러
 */
export const postApi = async (url, data) => {
  try {
    const response = await Api.post(url, data, {
      authRequired: true,
      headers: {},
    });
    return response;
  } catch (e) {
    return e;
  }
};

/**
 * POST 요청 (인증 불필요)
 * @param {string} url - API 엔드포인트
 * @param {object} data - 요청 본문 데이터
 * @returns {Promise} axios 응답 객체 (response.status, response.data) 또는 에러
 */
export const openPostApi = async (url, data) => {
  try {
    const response = await noInterceptorApi.post(url, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (e) {
    return e;
  }
};

/**
 * PUT 요청 (인증 필요)
 * @param {string} url - API 엔드포인트
 * @param {object} data - 요청 본문 데이터
 * @returns {Promise} 응답 데이터 또는 에러
 */
export const putApi = async (url, data) => {
  try {
    const response = await Api.put(url, data, {
      authRequired: true,
      headers: {},
    });
    return response;
  } catch (e) {
    return e;
  }
};

/**
 * DELETE 요청 (인증 필요)
 * @param {string} url - API 엔드포인트
 * @returns {Promise} 응답 데이터 또는 에러
 */
export const deleteApi = async (url) => {
  try {
    const response = await Api.delete(url, {
      authRequired: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (e) {
    return e;
  }
};

/**
 * 로그아웃 및 저장소 정리 함수
 * - 모든 보안 저장소 데이터 삭제
 * - redirect 파라미터가 있으면 로그인 후 해당 페이지로 이동
 */
export const clearLocalStorageAndLogout = () => {
  // 모든 보안 저장소 데이터 삭제
  SecureStorage.clear();

  // redirect 파라미터 확인하여 로그인 페이지로 이동
  const redirect = SecureStorage.getItem('redirect');
  if (redirect === 'undefined' || redirect === null || redirect === 'null') {
    window.location.href = '/login';
  } else {
    window.location.href = `/login?redirect=${encodeURIComponent(redirect)}`;
  }
};

export default Api;
